/**
 * EntityCharacteristics.jsx
 * Display of entity characteristics (commune/EPCI)
 * Migration from front/src/components/EntityCharacteristics.js
 * Phase 4: Business components
 */
import { memo } from 'react';
import { Box, List, ListItem, Typography } from '@mui/material';
import { characteristicsText, risksText, getRisksHeading, getTerritoryLabel } from '../config/settings.js';
import { capitalizeFirstLetter } from '../utils/dataHelpers.js';
import { getRiskLabel } from '../utils/risksMapping.js';

/**
 * Entity characteristics display component
 * @param {Object} props - Component props
 * @param {Object} props.entity - Entity data
 * @param {string} props.entity.nom - Entity name
 * @param {string} props.entity.identifiant_insee - Entity INSEE code
 * @param {string} props.entity.code_recherche - Postal code (commune) or SIREN (EPCI)
 * @param {number} props.entity.population - Population
 * @param {string} props.entity.nom_departement - Department name
 * @param {Array} props.entity.risques - Risk list (technical identifiers)
 * @param {number} props.entity.alt_minimum - Minimum altitude
 * @param {number} props.entity.alt_maximum - Maximum altitude
 * @param {string} props.entity.type - Entity type (commune/EPCI)
 * @param {string} props.entity.domaine - Entity domain (e.g., 'Nouvelle-Calédonie')
 * @returns {JSX.Element} Characteristics component
 */
const EntityCharacteristics = ({
  entity: {
    nom,
    identifiant_insee,
    code_recherche,
    population,
    nom_departement,
    risques,
    type,
    domaine,
  } = {},
  ...props
}) => {
  // Display postal code for communes, SIREN for EPCIs
  const displayCode = type === 'commune' ? code_recherche : identifiant_insee;

  // Territory label after entity name (null for SMSB)
  const territoryLabel = getTerritoryLabel({ domaine, nom_departement });

  // Convert risk IDs to labels for display, excluding ICU
  const displayableRisks = risques
    ?.filter(riskId => riskId !== 'icu')
    .map(riskId => getRiskLabel(riskId, domaine, risksText)) || [];

  // Check if ICU risk is present
  const hasIcuRisk = risques?.includes('icu');

  return (
  <List {...props}>
    <ListItem>
      {nom && (
        <Typography gutterBottom component="div">
          {capitalizeFirstLetter(type)} :{' '}
          <Box fontWeight="fontWeightMedium" component="span">
            {nom} ({displayCode}){territoryLabel && ` \u2013 ${territoryLabel}`}
          </Box>
        </Typography>
      )}
    </ListItem>

    <ListItem>
      {population && (
        <Typography gutterBottom component="div">
          {characteristicsText.population} :{' '}
          <Box fontWeight="fontWeightMedium" component="span">
            {`${population} ${characteristicsText.habitants}`}
          </Box>
        </Typography>
      )}
    </ListItem>

    <ListItem>
      {displayableRisks.length > 0 && (
        <Typography gutterBottom component="div">
          {getRisksHeading(domaine)} :{' '}
          <Box fontWeight="fontWeightMedium" component="span">
            {displayableRisks.join(', ')}
          </Box>
        </Typography>
      )}
    </ListItem>

    <ListItem>
      {hasIcuRisk && (
        <Typography gutterBottom component="div">
          {characteristicsText.autreEnjeu} :{' '}
          <Box fontWeight="fontWeightMedium" component="span">
            {characteristicsText.ilot}
          </Box>
        </Typography>
      )}
    </ListItem>

  </List>
  );
};

EntityCharacteristics.displayName = 'EntityCharacteristics';

export default memo(EntityCharacteristics);
